// Proportional scale modal: rescale the whole design by percent or by a
// target width; everything physical follows, angles and ratios do not.

import { useState } from 'react'
import { useStore } from '../state/store'
import { scaleDocument } from '../core/scaleDocument'
import { IconClose, IconFit } from './icons'

const r1 = (v: number) => Math.round(v * 10) / 10

export function ScaleDialog() {
  const doc = useStore((s) => s.doc)
  const commit = useStore((s) => s.commit)
  const setOverlay = useStore((s) => s.setOverlay)
  const requestFit = useStore((s) => s.requestFit)
  const pushToast = useStore((s) => s.pushToast)
  const [pct, setPct] = useState('100')

  const factor = (() => {
    const p = parseFloat(pct)
    return Number.isFinite(p) && p > 0 ? p / 100 : null
  })()
  const newW = factor ? r1(doc.canvas.width * factor) : null
  const newH = factor ? r1(doc.canvas.height * factor) : null
  const outOfRange =
    newW !== null && newH !== null && (Math.max(newW, newH) > 2000 || Math.min(newW, newH) < 5)

  const setFromWidth = (w: number) => {
    if (w > 0) setPct(String(r1((w / doc.canvas.width) * 100)))
  }

  const apply = () => {
    if (!factor || factor === 1 || outOfRange) return
    const p = parseFloat(pct)
    commit(`Scale design to ${p}%`, (d) => scaleDocument(d, factor))
    requestFit()
    setOverlay(null)
    pushToast(`Design scaled to ${p}% (${newW} x ${newH} mm)`, 'ok')
  }

  return (
    <div className="dialog scale-dialog holo-frame" role="dialog" aria-modal="true" aria-label="Scale design">
      <div className="dialog-head">
        <IconFit size={15} />
        <h2>Scale design</h2>
        <button className="quiet icon-btn" onClick={() => setOverlay(null)} aria-label="Close (Esc)">
          <IconClose size={13} />
        </button>
      </div>
      <div className="dialog-body">
        <p className="hint" style={{ marginTop: 0 }}>
          Rescales everything physical in one undoable step: canvas, boundary, margins,
          controllers, sizes, spacing, paths, guides, and image rects. Angles, ratios, morph
          amounts, and thickness stay as they are.
        </p>
        <div className="row">
          <span className="row-label">Current</span>
          <span className="mono">
            {doc.canvas.width} x {doc.canvas.height} mm
          </span>
        </div>
        <div className="row">
          <span className="row-label">Scale</span>
          <input
            type="text"
            inputMode="decimal"
            className="mono"
            style={{ width: 64 }}
            value={pct}
            autoFocus
            aria-label="Scale percent"
            onChange={(e) => setPct(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && apply()}
          />
          <span className="vfield-unit">%</span>
          <span className="scale-quick">
            {[50, 75, 150, 200].map((p) => (
              <button key={p} className="quiet" onClick={() => setPct(String(p))}>
                {p}%
              </button>
            ))}
          </span>
        </div>
        <div className="row">
          <span className="row-label">Target W</span>
          <input
            type="text"
            inputMode="decimal"
            className="mono"
            style={{ width: 76 }}
            value={newW ?? ''}
            aria-label="Target width"
            onChange={(e) => setFromWidth(parseFloat(e.target.value))}
            onKeyDown={(e) => e.key === 'Enter' && apply()}
          />
          <span className="vfield-unit">mm</span>
        </div>
        <div className="row">
          <span className="row-label">Result</span>
          <span className={outOfRange ? 'mono warn' : 'mono'}>
            {newW !== null && newH !== null ? `${newW} x ${newH} mm` : 'enter a scale'}
            {outOfRange && ' (outside 5 to 2000 mm)'}
          </span>
        </div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 10 }}>
          <button onClick={() => setOverlay(null)}>Cancel</button>
          <button className="primary" disabled={!factor || factor === 1 || outOfRange} onClick={apply}>
            Apply scale
          </button>
        </div>
      </div>
    </div>
  )
}
